import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { serviceLabel } from "@/components/booking/constants";
import { track } from "@/lib/track";
import { Switch } from "@/components/ui/switch";
import Navbar from "@/components/Navbar";
import StatusBadge from "@/components/StatusBadge";
import PodImage from "@/components/PodImage";
import TrackingTimeline from "@/components/TrackingTimeline";
import BreadcrumbMap from "@/components/BreadcrumbMap";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, MapPin, Copy, Navigation, Phone, CreditCard, FileText, Camera, X, AlertTriangle } from "lucide-react";
import { mapsDirUrl, telHref } from "@/lib/maps";
import { toast } from "sonner";
import EditOrderDialog from "@/components/EditOrderDialog";
import CancelOrderDialog from "@/components/CancelOrderDialog";
import SaveToDirectoryButton from "@/components/admin/SaveToDirectoryButton";
import OrderAttachments from "@/components/OrderAttachments";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const ADMIN_STATUSES = [
  { v: "paid", l: "Paid (back on board)" },
  { v: "assigned", l: "Assigned (on run)" },
  { v: "picked_up", l: "On board (picked up)" },
  { v: "in_transit", l: "In transit" },
  { v: "delivered", l: "Delivered" },
];

function SmsPrefToggle({ initialOptOut }) {
  const [optOut, setOptOut] = useState(initialOptOut);
  const toggleSms = async (checked) => {
    const next = !checked;
    setOptOut(next);
    try {
      await api.post("/auth/me/sms-preference", { opt_out: next });
      toast.success(checked ? "SMS delivery updates turned on" : "SMS delivery updates turned off");
    } catch {
      setOptOut(!next);
      toast.error("Could not save your preference");
    }
  };
  return (
    <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-sm text-muted-foreground" data-testid="sms-pref-toggle">
      <Switch checked={!optOut} onCheckedChange={toggleSms} data-testid="sms-pref-switch" />
      Text me delivery updates
    </label>
  );
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const downloadInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const res = await api.get(`/orders/${order.id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${order.tracking_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      track("invoice_downloaded");
    } catch {
      toast.error("Could not generate the invoice");
    } finally {
      setInvoiceLoading(false);
    }
  };

  useEffect(() => { api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => setOrder(false)); }, [orderId]);
  const reload = () => api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});

  const downloadNote = async () => {
    setNoteLoading(true);
    try {
      const res = await api.get(`/orders/${order.id}/consignment-note`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consignment-${order.tracking_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the consignment note");
    } finally {
      setNoteLoading(false);
    }
  };

  const payNow = async () => {    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
      if (data.paid_by_credit) {
        toast.success("Paid in full with your referral credit — no card needed!");
        const r = await api.get(`/orders/${order.id}`);
        setOrder(r.data);
        setPaying(false);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payment");
      setPaying(false);
    }
  };

  if (order === null) return <div className="min-h-screen bg-background"><Navbar /><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></div>;
  if (order === false) return <div className="min-h-screen bg-background"><Navbar /><p className="p-10 text-center text-muted-foreground">Order not found.</p></div>;

  const copyTrack = () => { navigator.clipboard.writeText(order.tracking_number); toast.success("Tracking number copied"); };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(user?.role === "driver" ? "/driver" : user?.role === "admin" ? "/admin" : "/dashboard")} data-testid="order-back-button" className="mb-4 gap-1.5">
          <ArrowLeft className="h-4 w-4" /> {user?.role === "driver" ? "Back to my jobs" : "Back to deliveries"}
        </Button>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green" data-testid="order-tracking-number">{order.tracking_number}</h1>
              <button onClick={copyTrack} data-testid="copy-tracking-button" className="text-muted-foreground hover:text-kilted-green"><Copy className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Booked {new Date(order.created_at).toLocaleDateString("en-GB")}</p>
            {user?.role === "customer" && <SmsPrefToggle initialOptOut={!!user?.sms_opt_out} />}
          </div>
          <div className="flex items-center gap-3">
            {(order.status === "pending_payment"
              || (user?.role === "admin" && !["delivered", "onforwarded", "cancelled"].includes(order.status))
              || (user?.role === "admin" && order.payment?.status === "on_account" && !order.payment?.billed && order.status !== "cancelled")
              || (user?.role === "driver" && ["assigned", "picked_up", "in_transit"].includes(order.status) && order.driver_id === user?.id))
              && <EditOrderDialog order={order} onSaved={reload} />}
            <CancelOrderDialog order={order} user={user} onCancelled={reload} />
            {user?.role === "admin" && order.customer_flags?.frequent_canceller && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700" title="Worth a call before dispatching" data-testid="order-frequent-canceller">
                <AlertTriangle className="h-3.5 w-3.5" /> Cancelled {order.customer_flags.cancels_30d} jobs in 30 days
              </span>
            )}
            {user?.role === "admin" && order.status !== "pending_payment" && (
              <Select value="" onValueChange={(v) => {
                const status = v === "paid" && order.billing === "account" ? "confirmed" : v;
                const label = ADMIN_STATUSES.find((s) => s.v === v)?.l || v;
                if (!window.confirm(`Set ${order.tracking_number} to "${label}"?${v === "delivered" ? " This marks it delivered without a POD." : ""}`)) return;
                api.post(`/admin/orders/${order.id}/set-status`, { status, note: "" })
                  .then(() => { toast.success(`Status set to ${label}`); reload(); })
                  .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
              }}>
                <SelectTrigger className="h-9 w-44 border-amber-400 text-amber-700" data-testid="admin-status-override">
                  <SelectValue placeholder="Override status…" />
                </SelectTrigger>
                <SelectContent>
                  {ADMIN_STATUSES.filter((s) => s.v !== order.status && !(s.v === "paid" && ["paid", "confirmed"].includes(order.status))).map((s) => (
                    <SelectItem key={s.v} value={s.v} data-testid={`override-status-${s.v}`}>{s.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <StatusBadge status={order.status} testid="order-status-badge" />
          </div>
        </div>

        {order.hold && <div className="mt-4"><HoldBanner order={order} user={user} onChanged={reload} /></div>}
        <AdjustmentBanner order={order} onResolved={reload} />

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Route</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <Addr label="Pickup" a={order.pickup} extra={user?.role === "admin" && <SaveToDirectoryButton address={order.pickup} which="pickup" />} />
              <div className="ml-2 h-4 border-l-2 border-dashed border-border" />
              <Addr label="Drop-off" a={order.dropoff} extra={user?.role === "admin" && <SaveToDirectoryButton address={order.dropoff} which="dropoff" instructions={order.delivery_instructions} />} />
              {order.driver_name && <p className="pt-2 text-sm"><span className="font-semibold">Courier:</span> {order.driver_name}</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Summary</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              {order.items?.length ? (
                <div className="space-y-1" data-testid="order-items">
                  <p className="text-muted-foreground">Items</p>
                  {order.items.map((it, i) => (
                    <div key={`${it.size}-${it.quantity}-${i}`} className="flex items-center justify-between pl-3">
                      <span className="capitalize">{it.quantity}× {it.size}{it.weight_kg ? ` · ${it.weight_kg}kg` : ""}</span>
                      {it.line_subtotal != null && <span className="font-medium">${it.line_subtotal.toFixed(2)}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <Line l="Parcel size" v={order.size} />
              )}
              <Line l="Service" v={serviceLabel(order.service)} />
              {order.service === "sameday" && (
                <p className={`text-xs font-semibold ${order.urgent_accepted_at ? "text-red-700" : "text-muted-foreground"}`} data-testid="order-urgent-status">
                  {order.urgent_accepted_at ? `Urgent job covered by ${order.urgent_accepted_by}` : "Urgent — waiting for a driver to accept"}
                </p>
              )}
              {(order.scheduled_date || order.pickup_window) && (
                <Line l="Scheduled pickup" testid="order-scheduled-pickup" v={[order.scheduled_date, order.pickup_window].filter(Boolean).join(" · ")} />
              )}
              <Line l="Authority to Leave" testid="order-atl" v={order.atl ? <span className="normal-case">Yes{order.atl_note ? <> — leave <b>{order.atl_note}</b></> : ""}</span> : <span className="normal-case">No — signature required</span>} />
              <Line l="Distance" v={`${order.distance_km} km`} />
              <Line l="Total weight" v={`${order.weight_kg} kg`} />
              {order.parcel_description && <Line l="Contents" v={order.parcel_description} />}
              <div className="my-2 border-t border-border" />
              {order.price.fuel_levy ? <Line l={`Fuel levy${order.price.fuel_levy_pct != null ? ` (${order.price.fuel_levy_pct}%)` : ""}`} v={`$${order.price.fuel_levy.toFixed(2)}`} /> : null}
              {order.price.gst != null && <Line l={`GST (${Math.round((order.price.gst_rate || 0.1) * 100)}%)`} v={`$${order.price.gst.toFixed(2)}`} />}
              {user?.role === "admin" && order.payment && (
                <Line l="Xero" testid="xero-sync-line" v={
                  order.payment.xero_sync === "invoiced"
                    ? `✓ Synced${order.payment.xero_invoice_number ? ` (${order.payment.xero_invoice_number})` : ""}`
                    : order.payment.status === "on_account" && !order.payment.billed
                      ? "⏳ Not yet invoiced"
                      : "— (card payment, receipted via Stripe)"
                } />
              )}
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total (inc GST)</span>
                <span className="font-heading text-lg font-extrabold text-kilted-green">${order.price.total.toFixed(2)}</span>
              </div>
              {order.payment.card_fee > 0 && (
                <Line l="Card processing fee (Stripe)" v={`+$${order.payment.card_fee.toFixed(2)}`} testid="order-card-fee" />
              )}
              {order.payment.credit_applied > 0 && (
                <>
                  <Line l="Referral credit applied" v={`−$${order.payment.credit_applied.toFixed(2)}`} testid="order-credit-applied" />
                </>
              )}
              {(order.payment.card_fee > 0 || order.payment.credit_applied > 0) && (
                <Line l={order.billing === "account" ? "Billed to account" : "Amount charged"} v={`$${(order.payment.amount_charged ?? order.payment.amount).toFixed(2)}`} testid="order-amount-charged" />
              )}
              <p className="text-xs text-muted-foreground">Payment: {order.payment.status} · Xero: {order.payment.xero_sync || "pending"}</p>
              {order.payment?.status === "paid" && (
                <Button onClick={downloadInvoice} disabled={invoiceLoading} variant="outline" data-testid="download-invoice-button" className="mt-3 w-full gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
                  {invoiceLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Download invoice PDF
                </Button>
              )}
              {order.billing === "account" && (
                <Button onClick={downloadNote} disabled={noteLoading} variant="outline" data-testid="download-consignment-note-button" className="mt-3 w-full gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
                  {noteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Download consignment note
                </Button>
              )}
              {order.status === "pending_payment" && (
                <Button onClick={payNow} disabled={paying} data-testid="pay-now-button" className="mt-3 w-full gap-2 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay ${order.price.total.toFixed(2)} now
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6">
          <CardHeader><CardTitle className="text-base">Photos</CardTitle></CardHeader>
          <CardContent>
            <PhotosSection order={order}
              onChange={(p) => setOrder({ ...order, photos: [...(order.photos || []), p] })}
              onDelete={(pid) => setOrder({ ...order, photos: (order.photos || []).filter((x) => x.id !== pid) })} />
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader><CardTitle className="text-base">Files &amp; documents</CardTitle></CardHeader>
          <CardContent>
            <OrderAttachments order={order} onChanged={reload} />
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader><CardTitle className="text-base">Tracking</CardTitle></CardHeader>
          <CardContent>
            {order.onforward && (
              <p className="mb-4 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800" data-testid="onforward-note">
                Handed to <b>{order.onforward.carrier}</b>{order.onforward.reference ? <> — their reference <b>{order.onforward.reference}</b></> : null}. This delivery is now tracked manually with that carrier.
              </p>
            )}
            <BreadcrumbMap history={order.status_history} driverLocation={order.driver_location} />
            <TrackingTimeline history={order.status_history} />
            {order.proof_of_delivery && (
              <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-sm font-semibold text-emerald-800">Proof of delivery</p>
                {order.proof_of_delivery.atl ? (
                  <div className="text-sm text-emerald-700" data-testid="pod-atl-note">
                    <p>Left in a safe place under your <b>Authority to Leave (ATL)</b> — no signature was collected, as you requested when booking.</p>
                    <p className="mt-1 text-xs text-emerald-800/80">The photo below shows exactly where our courier left the parcel and is your proof of delivery. Prefer a signature next time? Just untick ATL when you book.</p>
                  </div>
                ) : <p className="text-sm text-emerald-700">Signed by {order.proof_of_delivery.signature_name}</p>}
                {order.proof_of_delivery.notes && <p className="text-sm text-emerald-700">{order.proof_of_delivery.notes}</p>}
                {order.proof_of_delivery.photo_url ? (
                  <PodImage url={order.proof_of_delivery.photo_url} />
                ) : order.proof_of_delivery.photo_base64 ? (
                  <img src={order.proof_of_delivery.photo_base64} alt="Proof of delivery" className="mt-2 max-h-48 rounded-md border" />
                ) : null}
                {order.proof_of_delivery.signature_url && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-emerald-800">{order.proof_of_delivery.atl ? "Signature block" : "Recipient signature"}</p>
                    <PodImage url={order.proof_of_delivery.signature_url} alt="Recipient signature" testid="pod-signature-image" className="mt-1 max-h-28 rounded-md border bg-white" />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function AdjustmentBanner({ order, onResolved }) {
  const adj = order.price_adjustment;
  const [busy, setBusy] = useState(false);
  if (!adj || !["pending", "awaiting_choice"].includes(adj.status)) return null;

  const payDiff = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/adjustment/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment");
      setBusy(false);
    }
  };

  const resolve = async (choice) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/adjustment/resolve`, { choice });
      toast.success(choice === "refund"
        ? `$${data.amount.toFixed(2)} refunded to your card — allow 5-10 business days`
        : `$${data.amount.toFixed(2)} added as store credit for your next booking`);
      onResolved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (adj.type === "increase") {
    return (
      <div data-testid="adjustment-increase-banner" className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-4">
        <p className="text-sm font-semibold text-amber-900">Your order was updated — new total ${adj.new_total.toFixed(2)} (was ${adj.old_total.toFixed(2)})</p>
        <p className="mt-1 text-xs text-amber-800">A ${adj.amount.toFixed(2)} difference is due to confirm the change.</p>
        <Button onClick={payDiff} disabled={busy} data-testid="pay-difference-button" className="mt-3 gap-2 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay ${adj.amount.toFixed(2)} difference
        </Button>
      </div>
    );
  }
  return (
    <div data-testid="adjustment-decrease-banner" className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 p-4">
      <p className="text-sm font-semibold text-emerald-900">Good news — your order now costs ${adj.new_total.toFixed(2)} (was ${adj.old_total.toFixed(2)})</p>
      <p className="mt-1 text-xs text-emerald-800">You're owed ${adj.amount.toFixed(2)}. How would you like it back?</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => resolve("refund")} disabled={busy} data-testid="choose-refund-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Refund to my card
        </Button>
        <Button onClick={() => resolve("credit")} disabled={busy} variant="outline" data-testid="choose-credit-button" className="gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
          Store credit (use on next booking)
        </Button>
      </div>
    </div>
  );
}


function HoldBanner({ order, user, onChanged }) {
  const [busy, setBusy] = useState(false);
  const release = async (billToAccount) => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/release-hold`, { bill_to_account: billToAccount });
      toast.success(billToAccount ? "Hold released — confirmed and billed to account" : "Hold released");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const unpaid = order.status === "pending_payment";
  if (user?.role === "admin") {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900" data-testid="hold-banner-admin">
        <p className="font-semibold">On hold — {order.hold.cancels_30d} cancellations in the last 30 days</p>
        <p className="mt-0.5 text-xs">{order.hold.prepay_required ? "Account billing paused: the customer must prepay by card, or you confirm by phone and release the hold." : "Call the customer to confirm before this goes on the board."} It can't be dragged into a run until released.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => release(false)} disabled={busy} data-testid="release-hold-button" className="h-8 border-red-300 bg-white text-red-800 hover:bg-red-100">Confirmed by phone — release hold</Button>
          {order.hold.prepay_required && unpaid && <Button size="sm" onClick={() => release(true)} disabled={busy} data-testid="release-hold-account-button" className="h-8 bg-kilted-green hover:bg-kilted-greenLight">Release & bill to account</Button>}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="hold-banner-customer">
      <p className="font-semibold">This booking needs confirmation before it goes on the board</p>
      <p className="mt-0.5 text-xs">Because of recent cancellations on your account, {order.hold.prepay_required ? "this job needs to be prepaid by card (account billing is paused for it)" : "we'll give you a quick call to confirm"} before a driver is assigned. Paying now releases it straight away — or call us on 0408 464 106.</p>
    </div>
  );
}

function Addr({ label, a, extra }) {
  const key = label.toLowerCase().replace(/[^a-z]/g, "");
  return (
    <div className="flex gap-2">
      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-kilted-gold" />
      <div>
        <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
        <p data-testid={`addr-kind-${key}`} className={a.business ? "font-semibold" : "text-xs font-medium italic text-muted-foreground"}>{a.business || "Residential"}</p>
        <a href={mapsDirUrl(a)} target="_blank" rel="noreferrer" data-testid={`maps-link-${key}`}
          className="inline-flex items-center gap-1 text-kilted-green underline underline-offset-2 hover:text-kilted-greenLight">
          {a.line1}, {a.city}, {a.postcode} <Navigation className="h-3.5 w-3.5" />
        </a>
        {a.contact_phone && (
          <p className="text-xs text-muted-foreground">
            {a.contact_name ? `${a.contact_name} · ` : ""}
            <a href={telHref(a.contact_phone)} data-testid={`tel-link-${key}`} className="inline-flex items-center gap-1 text-kilted-green hover:underline">
              <Phone className="h-3 w-3" />{a.contact_phone}
            </a>
          </p>
        )}
        {a.customer_reference && <p className="text-xs text-muted-foreground" data-testid={`customer-reference-${key}`}>Ref: {a.customer_reference}</p>}
        {extra && <div className="mt-1">{extra}</div>}
      </div>
    </div>
  );
}
function Line({ l, v, testid }) {
  return <div className="flex items-center justify-between" data-testid={testid}><span className="text-muted-foreground">{l}</span><span className="font-medium capitalize">{v}</span></div>;
}

function PhotosSection({ order, onChange, onDelete }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const photos = order.photos || [];

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "package");
      const { data } = await api.post(`/orders/${order.id}/photos`, fd);
      onChange(data);
      toast.success("Photo added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't upload the photo"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = async (pid) => {
    try {
      await api.delete(`/orders/${order.id}/photos/${pid}`);
      onDelete(pid);
      toast.success("Photo removed");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't remove that photo"); }
  };

  return (
    <div data-testid="order-photos-section">
      {photos.length === 0 ? (
        <p className="text-sm text-muted-foreground">No photos yet — add a shot of your package or a screenshot of the original order so nothing gets mixed up.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {photos.map((p) => (
            <div key={p.id} className="relative rounded-md border border-border p-2" data-testid={`order-photo-${p.id}`}>
              <button type="button" onClick={() => removePhoto(p.id)} data-testid={`order-photo-delete-${p.id}`}
                className="absolute right-1 top-1 z-10 rounded-full bg-white/90 p-1 text-muted-foreground shadow hover:text-red-600" title="Remove photo">
                <X className="h-3.5 w-3.5" />
              </button>
              <PodImage url={p.url} alt={p.kind} testid={`order-photo-img-${p.id}`} className="max-h-36 w-full rounded object-cover" />
              <p className="mt-1 text-[11px] text-muted-foreground capitalize">{p.kind} · {p.by_name} ({p.by_role})</p>
              {p.note && <p className="text-[11px] text-muted-foreground">{p.note}</p>}
            </div>
          ))}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="order-photo-input" />
      <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="order-add-photo-button"
        className="mt-3 w-full gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Add a photo (package / order screenshot)
      </Button>
    </div>
  );
}
